import { HttpClient, HttpParams } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { EMPTY, from, Observable, of, switchMap, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  type ChatMessage,
  ConnectionState,
  DisconnectReason,
  LocalAudioTrack,
  LocalVideoTrack,
  Participant,
  RemoteParticipant,
  RemoteTrack,
  Room,
  RoomEvent,
  Track,
  TranscriptionSegment,
  VideoPresets,
} from 'livekit-client';
import { ChatEntry, Role, SessionPayload, SessionResponse } from '../models/livekit.model';
import { Metrics } from '../models/metrics';
import { OAuthService } from './oauth.service';
import { AnalyticsService } from './analytics.service';
import { AnalyticsEvents } from '../models/analytics';

const TOKEN_STORAGE_KEY_PREFIX = 'lk_token_';
const AUDIENCE_TOPIC = 'lk.audience-chat';
const PANELIST_TOPIC = 'lk.chat';
const METRICS_TOPIC = 'lk.metrics';

interface StoredSession {
  token: string;
  livekitUrl: string;
}

@Injectable({
  providedIn: 'root',
})
export class LivekitService {
  private readonly http = inject(HttpClient);
  private readonly oauth = inject(OAuthService);
  private readonly analytics = inject(AnalyticsService);
  private readonly apiUrl = `${environment.apiUrl}/v1`;

  private room: Room | null = null;
  private isPttCapturing = false;
  private pttSegments: TranscriptionSegment[] = [];
  private drainTimerId: ReturnType<typeof setTimeout> | null = null;
  private readonly DRAIN_TIMEOUT_MS = 400;

  protected readonly _isThinking = signal(false);
  readonly isThinking = this._isThinking.asReadonly();
  readonly currentRole = signal<Role | null>(null);
  readonly connectionState = signal<ConnectionState>(ConnectionState.Disconnected);
  readonly messages = signal<ChatEntry[]>([]);
  readonly agentAudioTrack = signal<RemoteTrack | null>(null);
  readonly remoteVideoTrack = signal<RemoteTrack | null>(null);
  readonly remoteAudioTrack = signal<RemoteTrack | null>(null);
  readonly localParticipantIdentity = signal<string | null>(null);
  readonly agentSpeaking = signal(false);

  readonly pttTranscription = signal<string>('');
  readonly pttFinalTranscription = signal<string>('');
  readonly localVideoTrack = signal<LocalVideoTrack | null>(null);
  readonly localAudioTrack = signal<LocalAudioTrack | null>(null);
  readonly agentMessages = computed(() => this.messages().filter((e) => e.isAgent));

  /** Latest real-time metrics snapshot pushed by the agent (metrics viewer only). */
  private readonly _liveMetrics = signal<Metrics | null>(null);
  readonly liveMetrics = this._liveMetrics.asReadonly();

  private readonly remoteParticipants = signal<Map<string, RemoteParticipant>>(new Map());
  /** True once the room has reached the `Connected` state at least once during this attempt. */
  private readonly _hasConnected = signal(false);
  readonly isConnected = computed(() => this.connectionState() === ConnectionState.Connected);
  /** True when connected to the room but the ingest source has not joined yet. */
  readonly isWaiting = computed(() => this.isConnected() && !this.ingestReady());
  /** True when the room is fully operational and the panelist can interact. */
  readonly isReady = computed(() => this.isConnected() && this.ingestReady());
  readonly isDisconnected = computed(() => this.connectionState() === ConnectionState.Disconnected);
  /**
   * True only when the session was established and has since ended — as opposed to the
   * initial idle/connecting state, which is also `Disconnected`. Drives the
   * "Session terminated" screen so it never flashes before the first connection.
   */
  readonly isTerminated = computed(
    () => this.connectionState() === ConnectionState.Disconnected && this._hasConnected(),
  );

  /** True when the ingest participant (identity: 'ingest-client') is in the room. */
  readonly ingestReady = computed(() => this.remoteParticipants().has('ingest-client'));

  /**
   * True when at least one panelist is in the room.
   * Panelists are identified as non-agent participants that are neither
   * the ingest source ('ingest-client') nor the passive display ('cleanfeed').
   */
  readonly panelistReady = computed(() =>
    [...this.remoteParticipants().values()].some(
      (p) => !p.isAgent && p.identity !== 'ingest-client' && p.identity !== 'cleanfeed',
    ),
  );

  connect(payload: SessionPayload, options?: { autoSubscribe?: boolean }): Observable<void> {
    const { role, voiceEnabled } = payload;
    this.currentRole.set(role);
    // Fresh attempt: forget any previous "connected" state so the terminated
    // screen cannot show during this attempt's fetch/handshake.
    this._hasConnected.set(false);
    // Renew the session when voice is enabled, or when a contextMode is requested
    // (ingest resume/reset flow): the fresh /sessions call must be the last one
    // before joining so the chosen contextMode is the one the agent honours.
    const stored = voiceEnabled || payload.contextMode ? null : this.getStoredSession(role);
    const session$: Observable<StoredSession> =
      stored && this.isTokenValid(stored.token)
        ? of(stored)
        : this.fetchSession(payload).pipe(tap((session) => this.storeSession(role, session)));

    return session$.pipe(
      switchMap((session) => {
        const room = new Room({
          adaptiveStream: false,
        });
        this.room = room;
        this.bindRoomEvents(this.room);
        return from(
          this.room.connect(session.livekitUrl, session.token, {
            autoSubscribe: options?.autoSubscribe ?? true,
          }),
        ).pipe(
          tap(() => {
            console.log(`Connected to room ${room.name}`);
            const userId = room.localParticipant.identity;
            this.analytics.setUser(userId);
            this.localParticipantIdentity.set(userId);
            this.remoteParticipants.set(new Map(room.remoteParticipants));
          }),
        );
      }),
    );
  }

  getTokenStorageKey(role: Role): string {
    return `${TOKEN_STORAGE_KEY_PREFIX}${role}`;
  }

  hasValidStoredSession(role: Role): boolean {
    const session = this.getStoredSession(role);
    return !!session && this.isTokenValid(session.token);
  }

  private getStoredSession(role: Role): StoredSession | null {
    const raw = sessionStorage.getItem(this.getTokenStorageKey(role));
    if (!raw) return null;
    try {
      return JSON.parse(raw) as StoredSession;
    } catch {
      return null;
    }
  }

  private isTokenValid(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      // Require at least 30 s remaining to avoid connecting with an about-to-expire token
      return payload.exp > Date.now() / 1000 + 30;
    } catch {
      return false;
    }
  }

  private storeSession(role: Role, session: Pick<SessionResponse, 'token' | 'livekitUrl'>): void {
    sessionStorage.setItem(
      this.getTokenStorageKey(role),
      JSON.stringify({ token: session.token, livekitUrl: session.livekitUrl }),
    );
  }

  terminateSession(role: Role): Observable<void> {
    if (!this.room) {
      return EMPTY;
    }

    return this.oauth.getAccessToken().pipe(
      switchMap((token) =>
        token
          ? this.http.delete<void>(`${this.apiUrl}/sessions`, {
              headers: { Authorization: `Bearer ${token}` },
            })
          : EMPTY,
      ),
      tap(() => sessionStorage.removeItem(this.getTokenStorageKey(role))),
    );
  }

  cleanupAfterDisconnection(): Observable<void> {
    if (!this.room) {
      return EMPTY;
    }
    this.room.removeAllListeners();
    return from(this.room.disconnect()).pipe(
      tap(() => {
        this.room = null;
        this.messages.set([]);
        this.agentAudioTrack.set(null);
        this.remoteVideoTrack.set(null);
        this.remoteAudioTrack.set(null);
        this.localVideoTrack.set(null);
        this.localAudioTrack.set(null);
        this.localParticipantIdentity.set(null);
        this.currentRole.set(null);
        this.remoteParticipants.set(new Map());
        this._liveMetrics.set(null);
        this.connectionState.set(ConnectionState.Disconnected);
        this.analytics.clearUser();
      }),
    );
  }

  startIngest(deviceIds?: { videoDeviceId?: string; audioDeviceId?: string }): void {
    if (!this.room) return;
    this.room.localParticipant
      .setMicrophoneEnabled(true, { deviceId: deviceIds?.audioDeviceId })
      .then((pub) => {
        const track = pub?.track as LocalAudioTrack | undefined;
        if (track) this.localAudioTrack.set(track);
      })
      .catch((err) => console.error('microphone publish failed', err));
    this.room.localParticipant
      .setCameraEnabled(
        true,
        {
          deviceId: deviceIds?.videoDeviceId,
          resolution: VideoPresets.h1080.resolution,
        },
        {
          degradationPreference: 'maintain-resolution',
          simulcast: false,
          videoEncoding: VideoPresets.h1080.encoding,
        },
      )
      .then((pub) => {
        const track = pub?.track as LocalVideoTrack | undefined;
        if (track) {
          this.localVideoTrack.set(track);
        }
      })
      .catch((err) => console.error('camera publish failed', err));
  }

  stopIngest(): void {
    if (!this.room) return;
    this.room.localParticipant.setMicrophoneEnabled(false);
    this.room.localParticipant.setCameraEnabled(false);
    this.localVideoTrack.set(null);
    this.localAudioTrack.set(null);
  }

  sendSettings(settings: Record<string, unknown>): void {
    if (!this.room) return;
    this.room.localParticipant.sendText(JSON.stringify(settings), { topic: 'lk.settings' });
  }

  sendMessage(text: string): void {
    if (!this.room) {
      return;
    }

    const room = this.room;
    this._isThinking.set(true);
    const message = {
      message: {
        message: text,
        timestamp: Date.now(),
        id: crypto.randomUUID(),
      },
      senderIdentity: room.localParticipant.identity,
      isAgent: false,
    };
    this.analytics.trackEvent(AnalyticsEvents.LiveChatMessageSent);
    if (this.currentRole() === 'panelist') {
      from(room.localParticipant.sendText(text, { topic: PANELIST_TOPIC })).subscribe({
        next: () => this.messages.update((entries) => [...entries, message]),
      });
    } else if (this.currentRole() === 'audience') {
      const agentIdentities = Array.from(room.remoteParticipants.values())
        .filter((p) => p.isAgent)
        .map((p) => p.identity);
      from(
        room.localParticipant.sendText(text, {
          topic: AUDIENCE_TOPIC,
          destinationIdentities: agentIdentities,
        }),
      ).subscribe({
        next: () => this.messages.update((entries) => [...entries, message]),
      });
    }
  }

  startPTT(): void {
    if (!this.room) return;
    this.room.localParticipant.setMicrophoneEnabled(true);
    this.cancelDrainTimer();
    this.pttSegments = [];
    this.pttTranscription.set('');
    this.isPttCapturing = true;
  }

  stopPTT(): void {
    if (!this.room) return;
    this.room.localParticipant.setMicrophoneEnabled(false);
    this.isPttCapturing = false;
    this.resetDrainTimer();
  }

  /**
   * Requests a session token. Public so callers (e.g. the ingest page) can probe
   * `hasExistingContext` before deciding on a `contextMode` and (re)connecting.
   */
  fetchSession(payload: SessionPayload): Observable<SessionResponse> {
    let params = new HttpParams().set('role', payload.role);
    if (payload.participantName) {
      params = params.set('participantName', payload.participantName);
    }
    if (payload.voiceEnabled) {
      params = params.set('voice', payload.voiceEnabled);
    }
    if (payload.maxWords) {
      params = params.set('maxWords', payload.maxWords);
    }
    if (payload.email) {
      params = params.set('email', payload.email);
    }
    if (payload.contextMode) {
      params = params.set('contextMode', payload.contextMode);
    }
    return this.http.get<SessionResponse>(`${this.apiUrl}/sessions`, {
      params,
    });
  }

  private bindRoomEvents(room: Room): void {
    room
      .on(RoomEvent.ConnectionStateChanged, (state: ConnectionState) => {
        this.connectionState.set(state);
        if (state === ConnectionState.Connected) this._hasConnected.set(true);
      })
      .on(RoomEvent.TrackSubscribed, (track: RemoteTrack, _pub, participant) => {
        if (track.kind === Track.Kind.Audio && participant.isAgent) {
          this.agentAudioTrack.set(track);
        }
        if (track.kind === Track.Kind.Video && participant.identity === 'ingest-client') {
          this.remoteVideoTrack.set(track);
        }
        if (track.kind === Track.Kind.Audio && participant.identity === 'ingest-client') {
          this.remoteAudioTrack.set(track);
        }
      })
      .on(RoomEvent.TrackUnsubscribed, (track: RemoteTrack, _pub, participant) => {
        if (participant.isAgent) {
          this.agentAudioTrack.set(null);
        }
        if (track.kind === Track.Kind.Video && participant.identity === 'ingest-client') {
          this.remoteVideoTrack.set(null);
        }
        if (track.kind === Track.Kind.Audio && participant.identity === 'ingest-client') {
          this.remoteAudioTrack.set(null);
        }
      })
      .on(RoomEvent.ActiveSpeakersChanged, (speakers: Participant[]) => {
        this.agentSpeaking.set(speakers.some((p) => p.isAgent));
      })
      .on(
        RoomEvent.TranscriptionReceived,
        (segments: TranscriptionSegment[], participant?: Participant) => {
          if (participant?.identity !== this.room?.localParticipant.identity) return;
          if (!this.isPttCapturing && this.drainTimerId === null) return;
          this.pttSegments = this.mergeSegments(segments, this.pttSegments);
          const text = this.pttSegments.map((s) => s.text).join(' ');
          this.pttTranscription.set(text);
          if (!this.isPttCapturing) {
            this.resetDrainTimer();
          }
        },
      )
      .on(RoomEvent.ParticipantConnected, (participant: RemoteParticipant) => {
        this.remoteParticipants.update(
          (map) => new Map([...map, [participant.identity, participant]]),
        );
      })
      .on(RoomEvent.ParticipantDisconnected, (participant: RemoteParticipant) => {
        this.remoteParticipants.update((map) => {
          const next = new Map(map);
          next.delete(participant.identity);
          return next;
        });
      })
      .on(RoomEvent.Disconnected, (reason?: DisconnectReason) => {
        this.cleanupAfterDisconnection().subscribe();
      });

    // restore chat history
    room.registerTextStreamHandler('lk.history', (reader) => {
      reader.readAll().then((text) => {
        try {
          const history = JSON.parse(text);
          console.debug(`Chat history: `, history);
          if (Array.isArray(history)) {
            this.messages.set(history);
          }
        } catch {
          // Do nothing if history is malformed
        }
      });
    });

    // Real-time metrics snapshots, pushed by the agent only to metrics viewers.
    if (this.currentRole() === 'metrics') {
      room.registerTextStreamHandler(METRICS_TOPIC, (reader) => {
        reader.readAll().then((text) => {
          try {
            this._liveMetrics.set(JSON.parse(text) as Metrics);
          } catch {
            // Ignore malformed metrics payload
          }
        });
      });
    }

    if (this.currentRole() === 'audience') {
      room.registerTextStreamHandler(AUDIENCE_TOPIC, async (reader) => {
        const answer = await reader.readAll();
        this.messages.update((entries) => [
          ...entries,
          {
            message: {
              message: answer,
              timestamp: Date.now(),
              id: crypto.randomUUID(),
            },
            senderIdentity: 'agent',
            isAgent: true,
          },
        ]);
        this._isThinking.set(false);
      });
    } else {
      room.on(
        RoomEvent.ChatMessage,
        (message: ChatMessage, participant?: RemoteParticipant | { identity: string }) => {
          const senderIdentity =
            participant?.identity ?? this.localParticipantIdentity() ?? 'unknown';
          const isAgent = participant instanceof RemoteParticipant ? participant.isAgent : false;
          if (isAgent) {
            this._isThinking.set(false);
          }
          this.messages.update((entries) => [...entries, { message, senderIdentity, isAgent }]);
        },
      );
    }
  }

  private resetDrainTimer(): void {
    if (this.drainTimerId !== null) clearTimeout(this.drainTimerId);
    this.drainTimerId = setTimeout(
      () => this.drainAndSetFinalTranscription(),
      this.DRAIN_TIMEOUT_MS,
    );
  }

  private cancelDrainTimer(): void {
    if (this.drainTimerId !== null) {
      clearTimeout(this.drainTimerId);
      this.drainTimerId = null;
    }
  }

  private drainAndSetFinalTranscription(): void {
    this.drainTimerId = null;
    const text = this.pttSegments
      .map((s) => s.text)
      .join(' ')
      .trim();
    this.pttFinalTranscription.set(text);
    this.pttSegments = [];
    this.pttTranscription.set('');
  }

  private mergeSegments(
    incoming: TranscriptionSegment[],
    existing: TranscriptionSegment[],
  ): TranscriptionSegment[] {
    const result = [...existing];
    for (const segment of incoming) {
      const idx = result.findIndex((s) => s.id === segment.id);
      if (idx > -1) result.splice(idx, 1, segment);
      else result.push(segment);
    }
    return result;
  }
}
